import React, { useEffect, useState } from "react";
import { MessageCircle, Phone, X } from "lucide-react";
import { useContent } from "@/context/ContentContext";

export default function FloatingContact() {
    const { content } = useContent();
    const [open, setOpen] = useState(false);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const onScroll = () => setVisible(window.scrollY > 400);
        window.addEventListener("scroll", onScroll);
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    if (!content) return null;

    const phoneDigits = content.phone.replace(/[^0-9]/g, "");
    const waMessage = encodeURIComponent(
        `Hi Three Brothers Vivero, I'm interested in your services.`
    );
    const waHref = content.whatsapp_url || `https://wa.me/${phoneDigits}?text=${waMessage}`;
    const contacts = content.contacts && content.contacts.length > 0
        ? content.contacts
        : [{ id: "main", name: "Call", phone: content.phone, role: "" }];

    return (
        <div
            className={`fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3 transition-all duration-500 ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8 pointer-events-none"
            }`}
            data-testid="floating-contact"
        >
            {open && (
                <div className="flex flex-col gap-3 animate-in slide-in-from-bottom-4 duration-300">
                    <a
                        href={waHref}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="floating-whatsapp"
                        className="group flex items-center gap-3 bg-white border border-tbv-border rounded-full pl-3 pr-5 py-2.5 shadow-xl hover:border-[#25D366] transition-all hover:translate-x-[-4px]"
                    >
                        <div className="w-9 h-9 rounded-full bg-[#25D366] flex items-center justify-center">
                            <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white" aria-hidden="true">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
                            </svg>
                        </div>
                        <div className="text-left">
                            <div className="text-[10px] uppercase tracking-wider text-tbv-soft font-bold leading-none mb-0.5">
                                Direct chat
                            </div>
                            <div className="font-display font-bold text-sm text-tbv-ink leading-none">
                                WhatsApp
                            </div>
                        </div>
                    </a>

                    {contacts.map((c, i) => (
                        <a
                            key={c.id || i}
                            href={`tel:${c.phone.replace(/[^+0-9]/g, "")}`}
                            data-testid={`floating-call-${i}`}
                            className="group flex items-center gap-3 bg-white border border-tbv-border rounded-full pl-3 pr-5 py-2.5 shadow-xl hover:border-tbv-deep transition-all hover:translate-x-[-4px]"
                        >
                            <div className="w-9 h-9 rounded-full bg-tbv-deep flex items-center justify-center">
                                <Phone className="w-4 h-4 text-white" />
                            </div>
                            <div className="text-left">
                                <div className="text-[10px] uppercase tracking-wider text-tbv-soft font-bold leading-none mb-0.5">
                                    {c.name}
                                </div>
                                <div className="font-display font-bold text-sm text-tbv-ink leading-none">
                                    {c.phone}
                                </div>
                            </div>
                        </a>
                    ))}
                </div>
            )}

            <button
                onClick={() => setOpen(!open)}
                data-testid="floating-contact-toggle"
                aria-label={open ? "Close contact" : "Open quick contact"}
                className={`relative w-16 h-16 rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 ${
                    open
                        ? "bg-tbv-ink text-white rotate-0"
                        : "bg-tbv-green text-tbv-dark hover:scale-110"
                }`}
            >
                {!open && (
                    <span className="absolute inset-0 rounded-full bg-tbv-green animate-ping opacity-40" />
                )}
                {open ? <X className="w-6 h-6 relative" /> : <MessageCircle className="w-7 h-7 relative" />}
            </button>
        </div>
    );
}
